import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Droplet, Droplets, Wind, Fingerprint, Radio,
  Bell, Activity, ScanLine, MapPin, Layers, Watch, Hand, Brain, ChevronDown,
} from "lucide-react";
import { VISION } from "@/lib/assets";
import { SectionHeading, Reveal } from "@/components/Section";
import { MarketConvergence } from "@/components/MarketConvergence";
import { CrossfadeBackground } from "@/components/CrossfadeBackground";
import { HeroStoryProvider } from "@/components/HeroStory";
import { SenseRelayBanner } from "@/components/SenseRelayBanner";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const VA = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";
const VISION_HERO_IMAGES = [
  { src: `${VA}9s45fuhp_wedding%20woman.jpg`, alt: "Woman in evening wear at a garden celebration at sunset wearing a WearSpray band", tf: "translateX(13%) scale(1.28)", posM: "46% 55%" },
  { src: `${VA}2l2fvj0u_lobby%20dude.jpg`, alt: "Man in a suit outside a grand hotel at night adjusting his WearSpray band", tf: "translateX(9%) scale(1.2)", posM: "52% 45%" },
  { src: `${VA}fc11xp9y_bondfire%20woman.jpg`, alt: "Woman by a beach bonfire at night wearing a WearSpray band", tf: "translateX(12%) scale(1.26)", posM: "54% 55%" },
  { src: `${VA}qm4hawqt_asian%20elegant%203.jpg`, alt: "Woman in a burgundy gown on a rooftop terrace at night wearing a WearSpray band", tf: "translateX(15%) scale(1.32)", posM: "70% 50%" },
];

const HERO_ANIM = {
  overline: { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } },
  title: { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 0.1 } },
  sub: { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 0.25 } },
};

const VBEAT = "block text-4xl sm:text-6xl md:text-7xl font-light leading-[1.02]";

// Vision hero, one sequence: the wrist is already smart; what if it could care for you too.
const VISION_STEPS = [
  {
    dur: null,
    headline: (
      <>
        <span className={VBEAT}>
          The wrist is <span className="text-accent">already smart</span>.
        </span>
        <span className={`${VBEAT} mt-2`}>
          What if it could <span className="text-accent">care for you too?</span>
        </span>
      </>
    ),
  },
];

const REVEAL_STEP_ANIM = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: "some" },
};

// Credibility tiers used across the page.
const TIERS = {
  available: { label: "Available Platform", color: "#00e5ff" },
  extension: { label: "Engineered Extension", color: "#e6c068" },
  concept: { label: "Future Concept", color: "#a1a1aa" },
};

function TierBadge({ tier, label, color, className = "" }) {
  const t = TIERS[tier] || {};
  const c = color || t.color;
  const l = label || t.label;
  return (
    <span
      className={`inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border ${className}`}
      style={{ borderColor: `${c}55`, color: c }}
      data-testid={`tier-${tier || "vision"}`}
    >
      <span className="w-1.5 h-1.5" style={{ background: c }} />
      {l}
    </span>
  );
}

function ImageTag({ label, className = "" }) {
  return (
    <span className={`absolute top-4 left-4 z-20 font-mono text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 bg-black/70 backdrop-blur border border-white/15 text-zinc-200 ${className}`}>
      {label}
    </span>
  );
}

const ASSEMBLY_SLIDES = [
  { src: `/sensor/exploded-1.jpg`, label: "Together" },
  { src: `/sensor/exploded-2.jpg`, label: "Lifting" },
  { src: `/sensor/exploded-3.jpg`, label: "Rising" },
  { src: `/sensor/exploded-4.jpg`, label: "Separating" },
  { src: `/sensor/exploded-5.jpg`, label: "Apart" },
];

const CORE_STAGES = [
  { n: "01", t: "Sense", d: "Understand what is happening on the body and in the environment.", c: "#00e5ff", Icon: Activity },
  { n: "02", t: "Understand", d: "Interpret body, environmental, or contextual information.", c: "#3ad6a0", Icon: ScanLine },
  { n: "03", t: "Prompt", d: "Provide a useful reminder or recommendation.", c: "#e6c068", Icon: Bell },
  { n: "04", t: "Act", d: "Immediate access to the physical solution already being carried.", c: "#ff2d78", Icon: Hand },
  { n: "05", t: "Learn", d: "Every use refines the next recommendation, personal to your skin and habits.", c: "#b794f6", Icon: Brain },
];

function CoreFlow() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % (CORE_STAGES.length + 1)), 900);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-px mt-14 bg-zinc-800 border border-zinc-800" data-testid="vision-core-flow">
      {CORE_STAGES.map((s, i) => {
        const on = i === active;
        const Icon = s.Icon;
        return (
          <div
            key={s.t}
            data-testid={`vision-core-stage-${i}`}
            className="relative p-6 md:p-8 h-full overflow-hidden transition-colors duration-700"
            style={{ backgroundColor: on ? "#171717" : "#121212" }}
          >
            <div
              className="absolute inset-x-0 top-0 h-[3px] transition-opacity duration-700"
              style={{ backgroundColor: s.c, opacity: on ? 1 : 0.15 }}
            />
            <div
              className="absolute -inset-10 blur-3xl pointer-events-none transition-opacity duration-700"
              style={{ background: `radial-gradient(circle at 50% 0%, ${s.c}44, transparent 60%)`, opacity: on ? 0.7 : 0 }}
            />
            <div className="relative flex items-center justify-between">
              <div className="relative w-9 h-9 flex items-center justify-center">
                {on && (
                  <span
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{ backgroundColor: `${s.c}33` }}
                  />
                )}
                <Icon
                  size={22}
                  strokeWidth={1.75}
                  className="relative transition-all duration-500"
                  style={{ color: on ? s.c : "#52525b", transform: on ? "scale(1.15)" : "scale(1)" }}
                />
              </div>
              <span className="font-mono text-xs transition-colors duration-500" style={{ color: on ? s.c : "#52525b" }}>{s.n}</span>
            </div>
            <h3 className="relative font-heading text-xl md:text-2xl font-medium mt-4 transition-colors duration-500" style={{ color: on ? s.c : "#ffffff" }}>{s.t}</h3>
            <p className="relative font-body text-sm leading-relaxed mt-3 transition-colors duration-500" style={{ color: on ? "#e4e4e7" : "#a1a1aa" }}>{s.d}</p>
          </div>
        );
      })}
    </div>
  );
}

function AssemblyCrossfade() {
  const [i, setI] = useState(0);
  useEffect(() => {
    // Preload every frame so the first cycle doesn't hitch on decode.
    ASSEMBLY_SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
    let dir = 1;
    let idx = 0;
    const id = setInterval(() => {
      if (idx >= ASSEMBLY_SLIDES.length - 1) dir = -1;
      else if (idx <= 0) dir = 1;
      idx += dir;
      setI(idx);
    }, 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative border border-zinc-800 overflow-hidden bg-black mt-4" data-testid="vision-biometric-exploded">
      <div className="relative w-full" style={{ aspectRatio: "1000 / 750" }}>
        {ASSEMBLY_SLIDES.map((s, idx) => (
          <img loading="lazy"
            key={s.src}
            src={s.src}
            alt={idx === 0 ? "WearSpray band fully assembled with the reservoir seated in the housing" : "The WearSpray reservoir separating from the band"}
            className="absolute inset-0 w-full h-full object-contain transition-opacity duration-[1800ms] ease-in-out"
            style={{ opacity: idx === i ? 1 : 0, willChange: "opacity" }}
            data-testid={`vision-assembly-slide-${idx}`}
          />
        ))}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 44% 46%, rgba(236,0,140,0.55), rgba(236,0,140,0.12) 38%, transparent 62%)", mixBlendMode: "screen" }}
        />
      </div>
      <span className="block font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-2.5 text-zinc-400 border-t border-zinc-800 bg-[#0a0a0a]">
        Assembly · Bezel · Reservoir · Sensor · Band
      </span>
    </div>
  );
}

function ChapterLabel({ n, label }) {
  return (
    <div className="flex items-center gap-4 mb-8">
      <span className="font-mono text-sm" style={{ color: "#00e5ff" }}>{n}</span>
      <span className="h-px flex-1 max-w-[60px] bg-zinc-700" />
      <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">{label}</span>
    </div>
  );
}

const WATCH_STEPS = [
  { step: "01", title: "Closed", src: "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/jmxzc76z_flip%20open%20watch.jpg", body: "A refined round timepiece on the wrist: time, date and status at a glance." },
  { step: "02", title: "Reveal & Dispense", src: "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/e0w38sfy_flip%20open%20watch%202.jpg", body: "A single hinged motion opens the face like a locket, revealing the round dispensing reservoir, ready on demand." },
];

const THREE_SYSTEMS = [
  { icon: Layers, title: "Topical Formulations", body: "Skincare, sunscreen, sanitizer, repellent, fragrance, serums, lotions and protective products." },
  { icon: Watch, title: "Wearable Hardware", body: "Smartwatches, wristbands, biometric devices and connected accessories on the body." },
  { icon: Hand, title: "Behavioral Access", body: "The ability to carry, dispense and reapply a formulation at the exact moment it is needed." },
];

// Digital capability exists to support dispensing, not to replace a general smartwatch.
const WEARSPRAY_ACTIONS = [
  { icon: Droplets, title: "Dispense on Demand", body: "Precision release the moment protection is needed." },
  { icon: Bell, title: "Reapplication Reminders", body: "A nudge to reapply, timed to the day and conditions." },
  { icon: Activity, title: "Track Refill Status", body: "Awareness of reservoir level and refill timing." },
  { icon: Wind, title: "Respond to Exposure", body: "React to environmental and activity inputs." },
  { icon: ScanLine, title: "Record Usage Events", body: "A quiet log of applications for personal or study insight." },
  { icon: MapPin, title: "Biometric & Location Context", body: "Application informed by the connected wearable ecosystem." },
];

const LUXURY_FEATURES = [
  { icon: Droplet, title: "Reservoir", body: "An active dispensing chamber that stores and delivers your liquid formulation cleanly and precisely." },
  { icon: Wind, title: "Dual-Function Micro Valve", body: "A dispensing and breathing element that controls release while letting the reservoir breathe for smooth, precise output." },
  { icon: Fingerprint, title: "Programmable Action Triggers", body: "Tactile control points assignable to platform actions such as dispense, reapplication reminder, or refill check." },
  { icon: Radio, title: "Phone + Voice Connection", body: "Connected intelligence and control, paired to your phone and the wider wearable ecosystem." },
];

const DESIGN_EXPRESSIONS = [
  { t: "Sporty", d: "Minimal, matte-finished bands for active luxury." },
  { t: "Glamorous", d: "Jeweled, crystal-set pieces for evening and occasion." },
  { t: "Collectible", d: "Limited-edition drops that make the refill an event." },
  { t: "Discreet", d: "Clean, architectural forms that disappear into daily life." },
];

const VLINE = "block text-3xl sm:text-4xl md:text-5xl font-light leading-[1.12]";

// Hero headline crossfade, the Vision narrative, playing out one line at a time.
const HERO_ROTATION = [
  <span className={`${VLINE} max-w-4xl`}>
    Personal care is too often <span className="text-accent">somewhere else</span> when you need it.
  </span>,
  <span className={`${VLINE} max-w-4xl`}>
    The problem is not knowing what to use. It is <span className="text-accent">having it when you need it</span>.
  </span>,
  <span className={`${VLINE} max-w-4xl`}>
    WearSpray puts the products people rely on <span className="text-accent">within immediate reach</span>.
  </span>,
  <span className={`${VLINE} max-w-4xl`}>
    On your wrist. Ready in an instant. <span className="text-accent">Protected. Prepared. Confident.</span>
  </span>,
  <span className={`${VLINE} max-w-4xl`}>
    Moving personal care from <span className="text-accent">carried to instantly within reach</span>.
  </span>,
  <span className={`${VLINE} max-w-4xl`}>
    Where Smart Hardware Meets{" "}
    <span className="text-accent">Physical Personal Care.</span>
  </span>,
];

const VISION_BANNER_CLIPS = [
  { src: "/prototype/vision-wave.mp4", poster: "/prototype/vision-wave-poster.jpg" },
  { src: "/prototype/vision-kayak.mp4", poster: "/prototype/vision-kayak-poster.jpg" },
];

function VisionWaveBanner() {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    const v = refs.current[active];
    if (v) {
      try {
        v.currentTime = 0;
        const pr = v.play();
        if (pr && pr.catch) pr.catch(() => {});
      } catch (error) {
        console.warn("Vision banner video play failed:", error);
      }
    }
  }, [active]);

  const handleEnded = () => setActive((a) => (a + 1) % VISION_BANNER_CLIPS.length);

  return (
    <section className="relative w-full bg-black border-y border-zinc-800 overflow-hidden" data-testid="vision-wave-banner">
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16 / 9", maxHeight: "80vh" }}>
        {VISION_BANNER_CLIPS.map((c, i) => (
          <video
            key={c.src}
            ref={(el) => (refs.current[i] = el)}
            src={c.src}
            poster={c.poster}
            autoPlay={i === 0}
            muted
            playsInline
            onEnded={handleEnded}
            data-testid={`vision-wave-video-${i}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out"
            style={{ opacity: active === i ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-16 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none">
          <span className="font-mono text-xs md:text-sm tracking-[0.28em] uppercase text-[#00e5ff] block mb-2">
            Always Within Reach
          </span>
          <h3 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-white max-w-2xl">
            Protection that moves with you.
          </h3>
        </div>
      </div>
    </section>
  );
}


export default function Vision() {
  const [pIdx, setPIdx] = useState(0);
  useEffect(() => {
    if (pIdx >= HERO_ROTATION.length - 1) return; // settle on the final banner headline
    const t = setTimeout(() => setPIdx((p) => p + 1), 3800);
    return () => clearTimeout(t);
  }, [pIdx]);
  return (
    <div data-testid="vision-page">
      <SEO {...PAGE_SEO["/vision"]} path="/vision" />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <HeroStoryProvider steps={VISION_STEPS}>
          <CrossfadeBackground images={VISION_HERO_IMAGES} />
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28">
            <motion.p
              {...HERO_ANIM.overline}
              className="font-mono text-xs tracking-[0.25em] uppercase mb-6"
              style={{ color: "#00e5ff" }}
              data-testid="vision-hero"
            >
              The Vision · The Wearable Delivery Platform
            </motion.p>

            <div className="max-w-4xl mt-6 md:mt-10">
              <h1 className="font-heading text-4xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[1.02]" data-testid="vision-hero-headline">
                Where intelligence meets{" "}
                <span className="text-accent">physical action.</span>
              </h1>
            </div>

            <motion.p
              {...HERO_ANIM.sub}
              className="font-body text-base md:text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed"
            >
              WearSpray is building toward a future where personal care,
              protection, and wearable intelligence exist within the same
              everyday ecosystem. Wearables already sense, measure, track, and
              communicate. WearSpray adds another possibility: immediate physical
              access to something the user can actually apply.
            </motion.p>
            <motion.p
              {...HERO_ANIM.sub}
              className="font-body text-sm md:text-base text-accent mt-5 max-w-2xl leading-relaxed"
            >
              The platform exists today. The vision is what it can become.
            </motion.p>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-8">
              <Link
                to="/patents"
                data-testid="vision-patents-link"
                className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-accent hover:opacity-80 transition-opacity"
              >
                See the patents behind a first of its kind
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="/docs/Combating-Malaria-with-WearSpray-Technology.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vision-whitepaper-link"
                className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-300 hover:text-white transition-colors"
              >
                Backed by research
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Scroll cue */}
          <div
            className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 pointer-events-none"
            data-testid="vision-scroll-cue"
          >
            <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-400">
              Scroll to explore
            </span>
            <ChevronDown size={18} className="text-accent animate-scroll-cue" />
          </div>
        </HeroStoryProvider>
      </section>

      {/* THE FRONTIER, WEARABLES ACT */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="vision-frontier">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="max-w-4xl space-y-3">
              <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.3] text-zinc-400">
                Wearables measure the body.
              </p>
              <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.3] text-zinc-400">
                They track the body.
              </p>
              <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.3] text-zinc-400">
                They communicate with the body.
              </p>
              <p
                className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.2] pt-6"
                style={{ color: "#00e5ff" }}
              >
                The next frontier is helping you act.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SENSE » UNDERSTAND » PROMPT » ACT, signature relay banner */}
      <SenseRelayBanner
        idPrefix="vision"
        eyebrow="The Shift Ahead"
        subtitle="Sensing is only the start. WearSpray closes the loop, turning what your wearable understands into something you can act on, right from the wrist."
      />

      {/* 4A · CONNECTED / SMART WEARABLE, moved up under hero/frontier */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="connected-vision">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-4 blur-3xl opacity-50 pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(230,192,104,0.18), transparent 65%)" }}
              />
              <div className="relative border border-zinc-800 overflow-hidden bg-black">
                <ImageTag label="Design Exploration" />
                <img loading="lazy"
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/lzf95l1h_asian%20girl%205.jpg"
                  alt="A model with purple-toned hair wearing a translucent WearSpray band with a glowing magenta face"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <TierBadge label="Connected Platform Vision" color="#00e5ff" className="mb-5" />
              <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-2xl">
                Protection that moves with you, and thinks with you.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body text-zinc-300 text-lg leading-relaxed mt-8">
                WearSpray was built on a simple premise: protection should move
                with you, not sit in a bag or on a shelf. We envision a dispensing
                wristband that delivers not just on demand, but intelligently
               , triggered by you, by exposure data, activity, or personal
                and environmental inputs from the wearable ecosystem it connects to.
              </p>
              <p className="font-body text-zinc-400 leading-relaxed mt-5">
                This is where we see the category heading: refillable, sustainable,
                connected delivery systems that compatible sun care, skincare,
                wellness, and protective formulations can be developed around.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE VISION, PLATFORM PREMISE */}
      <section id="chapter-vision" className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="vision-premise">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase block mb-8" style={{ color: "#00e5ff" }}>
              The Vision
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.08] max-w-4xl">
              The platform exists.{" "}
              <span style={{ color: "#e6c068" }}>The vision is what it can become.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-10 max-w-5xl">
            <Reveal delay={0.1}>
              <p className="font-body text-zinc-300 text-lg leading-relaxed">
                WearSpray began by solving the essential mechanical challenge:
                creating a refillable wearable dispensing system that could be
                pressed, manufactured, worn, and used to provide immediate access
                to a compatible formulation. That utility is now real.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-body text-zinc-300 text-lg leading-relaxed">
                The first WearSpray design demonstrates the mechanics; the patent
                portfolio establishes the platform. The same utility can extend
                into smartwatch integration, multiple reservoir configurations,
                adaptable wearable formats, connected systems, and partner-specific
                categories. What follows is a vision of that expansion.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug mt-12 pl-6 border-l-2 max-w-4xl" style={{ borderColor: "#00e5ff" }}>
              People will increasingly expect the products they rely on to be
              immediately accessible from the devices and wearables already
              connected to their lives.{" "}
              <span style={{ color: "#e6c068" }}>
                WearSpray is building the platform to make that future possible.
              </span>
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="font-heading text-lg md:text-xl font-light tracking-tight text-zinc-300 mt-10 max-w-3xl">
              The first WearSpray design proves the utility.{" "}
              <span style={{ color: "#00e5ff" }}>The Vision reveals the platform it can become.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FULL-WIDTH WAVE→KAYAK CROSSFADE BANNER, looped */}
      <VisionWaveBanner />

      {/* CHAPTER 1 · THE SHIFT */}
      <section id="chapter-shift" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="vision-thesis">
        <Reveal>
          <ChapterLabel n="01" label="The Shift" />
        </Reveal>
        <Reveal delay={0.05}>
          <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light leading-[1.35] tracking-tight max-w-5xl">
            For a century, skincare, protection, and daily health rituals lived
            in bottles, tubes, and bags. Used when we remembered. Left behind
            when we did not.
          </p>
          <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light leading-[1.35] tracking-tight max-w-5xl mt-6">
            The next decade rewrites that.
          </p>
          <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light leading-[1.35] tracking-tight max-w-5xl mt-6" style={{ color: "#e6c068" }}>
            Care is moving onto the body, into the rhythm of the day, and into
            the devices we already wear.
          </p>
        </Reveal>
      </section>

      {/* CHAPTER 2 · THE PLATFORM */}
      <section id="chapter-platform" className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="vision-platform">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <ChapterLabel n="02" label="The Platform" />
          </Reveal>
          {/* Platform statement, visually isolated */}
          <Reveal delay={0.05}>
            <div className="relative py-10 md:py-14">
              <div
                className="absolute -inset-x-6 inset-y-0 blur-3xl opacity-30 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at left center, rgba(0,229,255,0.14), transparent 70%)" }}
              />
              <p className="relative font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.02] max-w-5xl">
                We are building the{" "}
                <span style={{ color: "#00e5ff" }}>layer that connects them.</span>
              </p>
              <p className="relative font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-2xl">
                A wearable dispensing platform that brings care off the shelf and
                into the moment it is needed.
              </p>
            </div>
          </Reveal>

          {/* Three systems */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 bg-zinc-800 border border-zinc-800">
            {THREE_SYSTEMS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={i * 0.08}>
                  <div className="bg-[#121212] p-8 h-full hover:bg-[#161616] transition-colors duration-500">
                    <div className="w-11 h-11 border flex items-center justify-center" style={{ borderColor: "rgba(0,229,255,0.4)", color: "#00e5ff" }}>
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-6 block">0{i + 1}</span>
                    <h3 className="font-heading text-xl font-medium mt-2">{c.title}</h3>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug mt-16 pl-6 border-l-2 max-w-4xl" style={{ borderColor: "#00e5ff" }}>
              This is the convergence of behavior, ritual, health, utility, and
              protection.{" "}
              <span style={{ color: "#e6c068" }}>Worn.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* CHAPTER 3 · MARKET CONVERGENCE */}
      <div id="chapter-market" className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32">
          <Reveal>
            <ChapterLabel n="03" label="Market Convergence" />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug max-w-4xl">
              A platform aligned with where personal care and wearable technology
              are moving.
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5 max-w-3xl">
              WearSpray&rsquo;s core utility connects with established, growing
              categories, skincare, sun care, fragrance, hygiene, outdoor
              protection, and wearable technology. The market data below maps the
              landscape around the platform, and where immediate wearable access
              could create new products, partnerships, and licensing.
            </p>
          </Reveal>
        </div>
      </div>
      <MarketConvergence />

      {/* LINK OUT · WearSpray's own addressable audience (US/Europe/Caribbean) */}
      <section className="border-t border-zinc-800 bg-black" data-testid="vision-market-opportunity-link">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Reveal>
            <div className="border border-zinc-800 bg-[#0b0b0b] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-3">
                  A Different Question
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight max-w-xl">
                  The chart above sizes adjacent industries. How many actual people could
                  WearSpray reach first?
                </h3>
              </div>
              <Link
                to="/market"
                data-testid="vision-market-opportunity-cta"
                className="group shrink-0 inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-7 py-3.5 hover:bg-[#00b3cc] transition-colors"
              >
                See the addressable audience
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CHAPTER 4 · PLATFORM APPLICATIONS */}
      <section id="chapter-applications" className="border-t border-zinc-800" data-testid="vision-applications">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <ChapterLabel n="04" label="Platform Applications" />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.05] max-w-4xl">
              Not separate inventions.{" "}
              <span style={{ color: "#00e5ff" }}>Expressions of one protected platform.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
              Every direction below is built on the same patented dispensing core.
              Each is labeled by how far it sits from today's shipping product, so
              it is always clear what is available now and what is envisioned next.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FULL-WIDTH WAVE→KAYAK CROSSFADE BANNER, looped */}

      {/* THE CORE VISION · SENSE · UNDERSTAND · PROMPT · ACT */}
      <section
        className="border-t border-zinc-800 bg-[#0a0a0a] relative overflow-hidden"
        data-testid="vision-core"
      >
        <div
          className="absolute -top-1/4 right-[-10%] w-[55vw] h-[55vh] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.16), transparent 65%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">The Core Vision</span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.08] max-w-4xl">
              Wearables measure the body. They track it. They communicate with it.{" "}
              <span style={{ color: "#00e5ff" }}>The next frontier is helping you act.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.06}>
            <CoreFlow />
          </Reveal>

          <Reveal delay={0.08}>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight text-white mt-10">
              Sense. Understand. Prompt. Act. <span style={{ color: "#b794f6" }}>Learn.</span>
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 border-l-2 pl-6 md:pl-8 max-w-4xl" style={{ borderColor: "#00e5ff" }}>
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 block mb-6">
                For Example
              </span>
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-x-4 gap-y-4" data-testid="vision-imagine-flow">
                {["UV exposure rises.", "Wearable or phone detects context.", "You receive a reminder.", "Sunscreen is already within reach."].map((step, i, arr) => (
                  <div key={step} className="flex items-center gap-4">
                    <span
                      className="font-heading text-xl md:text-3xl font-light tracking-tight leading-snug"
                      style={i === arr.length - 1 ? { color: "#00e5ff" } : undefined}
                    >
                      {step}
                    </span>
                    {i < arr.length - 1 && <ArrowRight size={20} className="text-accent shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* SMARTWATCH FUTURE, one consolidated section */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <TierBadge label="Future Concept" color="#00e5ff" className="mb-6" />
          </Reveal>
          <SectionHeading
            overline="Smartwatch Future"
            title="When the smartwatch becomes an action platform."
            subtitle="Smart wearables already capture information about the user and their environment. WearSpray introduces the possibility of connecting that intelligence to immediate physical access, sensing, contextual information, reminders, and WearSpray dispensing within one wearable ecosystem."
            testId="reveal-heading"
          />
          <Reveal delay={0.1}>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
              {WATCH_STEPS.map((s, i) => (
                <motion.div
                  key={s.step}
                  {...REVEAL_STEP_ANIM}
                  transition={{ duration: 0.55, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className="relative border border-zinc-800 bg-black overflow-hidden group"
                >
                  <ImageTag label="Smartwatch Integration Direction" />
                  <img loading="lazy"
                    src={s.src}
                    alt={`Step ${s.step} · ${s.title}`}
                    className="w-full h-[440px] md:h-[560px] object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black via-black/70 to-transparent">
                    <span className="font-mono text-sm" style={{ color: "#e6c068" }}>{s.step}</span>
                    <h3 className="font-heading text-xl font-medium mt-1">{s.title}</h3>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1">{s.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-6 bg-zinc-800 border border-zinc-800" data-testid="vision-watch-capabilities">
            {[
              { t: "Sense", d: "Biometrics and environmental context." },
              { t: "Prompt", d: "Relevant reminders and guidance." },
              { t: "Dispense", d: "Immediate access to the carried formulation." },
              { t: "Record", d: "Application history and user-selected activity." },
            ].map((c) => (
              <div key={c.t} className="bg-[#121212] p-6 h-full">
                <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: "#00e5ff" }}>{c.t}</span>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAPTER 5 · FUTURE DESIGN */}
      <section id="chapter-design" className="border-t border-zinc-800" data-testid="vision-future-design">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <ChapterLabel n="05" label="Future Design" />
          </Reveal>
          <Reveal delay={0.05}>
            <TierBadge label="Design Exploration" color="#e6c068" className="mb-6" />
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.0] max-w-4xl">
              Where utility becomes{" "}
              <span style={{ color: "#e6c068" }}>luxury.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
              One expression of the platform, imagined at its most aspirational.
              The next era of beauty and protection will not only be carried. It
              will be worn as a beautiful object on the body.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LUXURY WATCH + FEATURES */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div
                className="absolute inset-0 blur-3xl opacity-50"
                style={{ background: "radial-gradient(circle, rgba(230,192,104,0.25), transparent 60%)" }}
              />
              <div className="relative">
                <ImageTag label="Design Exploration" />
                <img loading="lazy"
                  src={VISION.luxuryWatch}
                  alt="Luxury ruby-and-gold gem-set smartwatch dispenser concept"
                  className="relative w-full max-h-[78vh] object-contain mx-auto"
                />
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="font-mono text-xs tracking-[0.2em] uppercase mb-4 block" style={{ color: "#e6c068" }}>
                Concept · Haute Wearable
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
                Engineered to perform. Designed to be admired.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px mt-10 bg-zinc-800 border border-zinc-800">
              {LUXURY_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.title} delay={(i % 2) * 0.08}>
                    <div className="bg-[#121212] p-6 h-full hover:bg-[#161616] transition-colors duration-500">
                      <div className="w-10 h-10 border flex items-center justify-center" style={{ borderColor: "rgba(230,192,104,0.4)", color: "#e6c068" }}>
                        <Icon size={18} strokeWidth={1.5} />
                      </div>
                      <h3 className="font-heading text-base font-medium mt-4">{f.title}</h3>
                      <p className="font-body text-sm text-zinc-500 leading-relaxed mt-2">{f.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CONCEPT STUDIO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionHeading
          overline="Concept Studio"
          title="Designs the platform makes possible."
          subtitle="The same patented core, styled for how people actually live: bold, colorful and personal. One expression of the WearSpray platform as a fashion-forward statement piece."
          testId="concept-studio-heading"
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16 items-center">
          <Reveal className="lg:col-span-7">
            <div className="relative border border-zinc-800 bg-black overflow-hidden">
              <ImageTag label="Design Exploration" />
              <img loading="lazy"
                src="/vision-magenta.jpg?v=2"
                alt="A fashion-forward WearSpray concept worn as a bold magenta statement piece"
                className="w-full h-auto object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: "#e6c068" }}>
              Concept · Statement Piece
            </span>
            <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight mt-4 leading-snug">
              The same patented core, styled to make a statement.
            </h3>
            <p className="font-body text-zinc-400 leading-relaxed mt-4">
              Bold, colorful and personal: one expression of how the WearSpray
              platform can live as a fashion-forward accessory. The protected
              dispensing principle stays at the core; the look is entirely open.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FRAGRANCE, folded into beauty, one line */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="vision-fragrance">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Reveal>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-zinc-300">
              The same refillable architecture can extend into{" "}
              <span style={{ color: "#e6c068" }}>fragrance, body mist, skincare, and other beauty applications.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL VISION STATEMENT */}
      <section id="chapter-partner" className="border-t border-zinc-800 bg-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.12] max-w-4xl mx-auto">
              The next generation of wearables should not just tell us something.{" "}
              <span style={{ color: "#00e5ff" }}>They should help us do something.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-2xl mx-auto">
              WearSpray is building the physical action layer between intelligence
              and immediate personal care.
            </p>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight mt-8" style={{ color: "#00e5ff" }}>
              Sense. Understand. Prompt. Act.
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-12">
              <Link
                to="/contact"
                data-testid="vision-cta"
                className="font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 hover:-translate-y-0.5 transition-transform"
              >
                Partner With WearSpray
              </Link>
              <Link
                to="/products"
                data-testid="vision-cta-platform"
                className="font-body text-sm font-medium text-white border border-zinc-700 px-8 py-4 hover:border-accent hover:text-accent transition-colors"
              >
                Explore the Platform
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
